import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Center,
  useColorModeValue,
  Icon,
  Flex,
  AspectRatio,
  Button,
  Tooltip,
  Container,
  useBreakpointValue,
  Image,
  Stack,
  useDisclosure
} from '@chakra-ui/react';
import ReactPlayer from 'react-player/lazy';
import { motion, useViewportScroll, useTransform } from 'framer-motion';
import { FaPlayCircle, FaChevronDown, FaArrowRight, FaClock } from 'react-icons/fa';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionText = motion(Text);

const TutorialLabel = () => (
  <Box
    position="absolute"
    top="-10px"
    right="-10px"
    bg="blue.500"
    color="white"
    px={3}
    py={1}
    borderRadius="full"
    fontSize="sm"
    fontWeight="bold"
    boxShadow="lg"
    transform="rotate(15deg)"
    zIndex={2}
  >
    Tutorial
  </Box>
);

export default function Hero() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const videoId = "vh1hj4rqv88";
  const [thumbnail, setThumbnail] = useState('');

  const findBestThumbnail = useCallback(async () => {
    const qualities = ['maxresdefault', 'sddefault', 'hqdefault', '0'];
    for (const quality of qualities) {
      const url = `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
      const img = new window.Image();
      const isValid = await new Promise((resolve) => {
        img.onload = () => resolve(img.width > 120);
        img.onerror = () => resolve(false);
        img.src = url;
      });
      if (isValid) {
        setThumbnail(url);
        break;
      }
    }
  }, [videoId]);

  useEffect(() => {
    findBestThumbnail();
  }, [findBestThumbnail]);

  const scrollToFeatures = () => {
    const featuresSection = document.getElementById('features');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const { scrollY } = useViewportScroll();
  const y1 = useTransform(scrollY, [0, 300], [0, -50]);

  const isFullHeight = useBreakpointValue({ base: false, lg: true });
  const showPlayOverlay = useBreakpointValue({ base: true, md: true });
  const bgGradient = useColorModeValue(
    'linear(to-br, blue.50, blue.100)',
    'linear(to-br, blue.900, purple.900)'
  );
  const textColor = useColorModeValue('gray.800', 'white');
  const headingColor = useColorModeValue('blue.600', 'blue.300');
  const textContentColor = useColorModeValue('gray.700', 'gray.300');
  const buttonColor = useColorModeValue('blue.600', 'blue.300');
  const buttonHoverBg = useColorModeValue('blue.100', 'whiteAlpha.200');

  return (
    <MotionBox
      as="section"
      minHeight="100vh"
      width="100%"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      bgGradient={bgGradient}
      color={textColor}
      position="relative"
      overflow="hidden"
      py={{ base: 16, md: 20, lg: 28 }}
    >
      <Container maxW="1600px" h="full" display="flex" flexDirection="column" alignItems="center">
        {/* Logo */}
        <Box width="300px" height="100px" position="relative" mb={8}>
          <Image
            src="/kolibri_icon.png"
            alt="Quodara Chrono Logo"
            objectFit="contain"
            width="100%"
            height="100%"
          />
        </Box>

        <MotionFlex
          direction={{ base: 'column', lg: 'row' }}
          align="center"
          justify="space-between"
          w="full"
          h="full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <VStack
            spacing={{ base: 6, md: 8 }}
            align={{ base: 'center', lg: 'flex-start' }}
            textAlign={{ base: 'center', lg: 'left' }}
            w={{ base: 'full', lg: '45%' }}
            mb={{ base: 12, lg: 0 }}
          >
            <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Heading
                as="h1"
                fontSize={{ base: '4xl', md: '5xl', lg: '6xl' }}
                fontWeight="extrabold"
                letterSpacing="tight"
                color={headingColor}
                lineHeight="1.2"
                mb={4}
              >
                Quodara Chrono
              </Heading>
            </MotionBox>
            
            <MotionText
              fontSize={{ base: 'xl', md: '2xl', lg: '3xl' }}
              color={textContentColor}
              lineHeight="1.4"
              mb={6}
              fontWeight="semibold"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Revolutioniere deine Zeiterfassung
            </MotionText>
            
            <MotionText
              fontSize={{ base: 'md', md: 'lg' }}
              color={textContentColor}
              lineHeight="1.8"
              maxWidth="600px"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              Schluss mit Excel-Chaos! Entdecke unsere intuitive, webbasierte Technologie für präzises Zeitmanagement. Einfach, effizient und überall verfügbar – optimiere jetzt deine Produktivität.
            </MotionText>

            <Stack
              direction={{ base: 'column', sm: 'row' }}
              spacing={4}
              align="center"
              justify={{ base: 'center', lg: 'flex-start' }}
              w="full"
              mt={8}
            >
              <Button
                size="lg"
                colorScheme="blue"
                rightIcon={<FaArrowRight />}
                onClick={onOpen}
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                transition="all 0.3s"
              >
                Tutorial ansehen
              </Button>
              <Button
                size="lg"
                variant="outline"
                colorScheme="blue"
                onClick={scrollToFeatures}
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                transition="all 0.3s"
              >
                Features entdecken
              </Button>
            </Stack>
          </VStack>
          
          <Box
            w={{ base: 'full', lg: '50%' }}
            maxW="800px"
            position="relative"
          >
            <AspectRatio ratio={16 / 9}>
              <MotionBox
                boxShadow="2xl"
                borderRadius="xl"
                overflow="hidden"
                position="relative"
                cursor="pointer"
                onClick={onOpen}
                whileHover={{ scale: 1.02 }}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ 
                  duration: 0.8, 
                  delay: 0.2,
                  hover: { duration: 0.3 }
                }}
                role="group"
              >
                {thumbnail && (
                  <Image
                    src={thumbnail}
                    alt="Video Thumbnail"
                    objectFit="cover"
                    w="100%"
                    h="100%"
                  />
                )}
                {showPlayOverlay && (
                  <Center
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    bg="rgba(0, 0, 0, 0.3)"
                    opacity={0}
                    transition="all 0.3s"
                    _groupHover={{ opacity: 1 }}
                  >
                    <Icon
                      as={FaPlayCircle}
                      boxSize={{ base: 16, md: 20, lg: 24 }}
                      color="white"
                      opacity={0.9}
                      transition="all 0.3s"
                      _groupHover={{ transform: 'scale(1.1)' }}
                    />
                  </Center>
                )}
              </MotionBox>
            </AspectRatio>
            <TutorialLabel />
          </Box>
        </MotionFlex>
      </Container>

      <MotionBox
        position="absolute"
        top="10%"
        left="5%"
        style={{ y: y1 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.6 }}
        transition={{ duration: 1, delay: 0.5 }}
      >
        <Icon as={FaClock} boxSize={16} color={useColorModeValue('blue.200', 'blue.700')} />
      </MotionBox>

      {isFullHeight && (
        <Tooltip label="Zu den Features scrollen" placement="top" hasArrow>
          <MotionBox
            position="absolute"
            bottom="5%"
            left="50%"
            transform="translateX(-50%)"
            initial={{ y: 0 }}
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <Button
              leftIcon={<FaChevronDown />}
              variant="ghost"
              color={buttonColor}
              bg="transparent"
              _hover={{ bg: buttonHoverBg }}
              onClick={scrollToFeatures}
            >
              Features
            </Button>
          </MotionBox>
        </Tooltip>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <ModalOverlay />
        <ModalContent w="100vw" h="100vh" m="0">
          <ModalCloseButton size="lg" zIndex="modal" />
          <ModalBody p={0} display="flex" alignItems="center" justifyContent="center">
            <AspectRatio ratio={16 / 9} w="100%" maxW="1600px" maxH="90vh">
              <ReactPlayer
                url={`https://www.youtube.com/watch?v=${videoId}`}
                width="100%"
                height="100%"
                controls={true}
              />
            </AspectRatio>
          </ModalBody>
        </ModalContent>
      </Modal>
    </MotionBox>
  );
}
